import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

/**
 * Camada modal do painel, em duas formas — e as duas existem por motivos
 * diferentes.
 *
 * - `bottom` é a FOLHA: sobe da borda de baixo, ocupa a largura toda no
 *   celular e flutua como cartão no desktop. É para conteúdo que a pessoa
 *   precisa LER antes de decidir (a revisão do projeto). Ela pode ser
 *   arrastada para baixo, porque o conteúdo é longo e o polegar já está lá.
 * - `center` é o ALERTA: pequeno, no meio, cresce a partir do centro. É para
 *   uma pergunta de uma linha com resposta destrutiva (descartar o rascunho).
 *   Não se arrasta: fechar por engano uma pergunta é bom, mas uma folha que
 *   escorrega do dedo em cima de "Descartar" não é.
 *
 * Escrito à mão, sem Radix (regra 13): o que um `Dialog` de biblioteca traz e
 * que precisa existir aqui são quatro coisas — foco preso dentro do painel,
 * `Esc`, rolagem do fundo travada e foco devolvido a quem abriu. Estão todas
 * abaixo, e nenhuma delas é grande o bastante para justificar uma dependência.
 *
 * O que NÃO está aqui, de propósito: `aria-modal` sem `role="dialog"`, painel
 * que fecha durante uma ação irreversível (`dismissible={false}` enquanto o
 * envio corre) e animação que trave a entrada — a folha é agarrável no meio do
 * caminho, e é isso que a faz parecer objeto em vez de transição.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Tempo máximo que o painel fica montado depois de fechado (rede de segurança). */
const EXIT_MS = 280;

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Uma linha dizendo o que a pessoa decide aqui. Vira o `aria-describedby`. */
  description?: string;
  placement?: 'bottom' | 'center';
  /**
   * Esconde o cabeçalho VISUAL mantendo o nome acessível do diálogo.
   *
   * É o estado de confirmação ("Projeto enviado"), em que o painel inteiro
   * vira uma coisa só, centrada. Um título alinhado à esquerda com um visto
   * centrado embaixo são duas composições disputando a mesma folha.
   */
  titleHidden?: boolean;
  /**
   * Enquanto `false`, nem `Esc`, nem clique no fundo, nem arrasto fecham. É o
   * estado de "já apertei enviar": fechar no meio deixaria a pessoa sem saber
   * se o projeto foi.
   */
  dismissible?: boolean;
  /** Barra de ação, fixa no pé do painel — nunca rola junto com o conteúdo. */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  placement = 'bottom',
  titleHidden = false,
  dismissible = true,
  footer,
  children,
  className,
}: SheetProps) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  /** Fechou por arrasto: a saída já está sendo animada em `transform` inline. */
  const draggedOutRef = useRef(false);

  const titleId = useId();
  const descriptionId = useId();

  const isDraggable = placement === 'bottom' && dismissible;

  const requestClose = useCallback(() => {
    if (!dismissible) return;
    onClose();
  }, [dismissible, onClose]);

  /* Montagem atrasada na saída: sem isto o painel some no frame do clique e a
     folha nunca desce — o movimento de saída é o que diz de onde a tela voltou. */
  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;

    setClosing(true);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
      draggedOutRef.current = false;
    }, EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  /* Rolagem do fundo travada e foco devolvido a quem abriu. A devolução é a
     parte que costuma faltar: sem ela, quem navega por teclado volta ao topo
     do documento e precisa tabular o formulário inteiro de novo. */
  useEffect(() => {
    if (!mounted) return;

    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // O foco vai para o PAINEL, não para o primeiro botão. Mandar para o
    // primeiro focável acende o anel em cima do "X" — a folha abriria com a
    // saída em destaque, e quem lê acha que algo está selecionado. Do painel,
    // `Tab` entra no conteúdo na ordem natural e `Esc` já funciona.
    panelRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus?.({ preventScroll: true });
    };
  }, [mounted]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      requestClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusables = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
    ).filter((element) => element.offsetParent !== null);
    if (focusables.length === 0) return;

    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const drag = useSheetDrag({
    enabled: isDraggable,
    panelRef,
    onDismiss: () => {
      draggedOutRef.current = true;
      onClose();
    },
  });

  if (!mounted) return null;

  const isBottom = placement === 'bottom';

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex',
        isBottom ? 'items-end justify-center sm:p-4' : 'items-center justify-center p-4',
      )}
    >
      {/* Escurecer para focar (§ material): o formulário continua visível
          atrás, desfocado, porque a revisão é sobre ELE — esconder o contexto
          faria a folha parecer outra tela. */}
      <div
        aria-hidden="true"
        onClick={requestClose}
        className={cn(
          'absolute inset-0 bg-foreground/25 backdrop-blur-[2px]',
          closing ? 'animate-fade-out' : 'animate-fade-in',
        )}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative flex max-h-[88vh] w-full flex-col overflow-hidden bg-card text-card-foreground outline-none',
          'shadow-[0_-1px_2px_hsl(222_24%_12%/0.04),0_-12px_40px_-12px_hsl(222_24%_12%/0.28)]',
          isBottom
            ? 'max-w-2xl rounded-t-[1.25rem] sm:rounded-[1.25rem]'
            : 'max-w-md rounded-[1.25rem]',
          // Durante o arrasto o `transform` é inline e 1:1 com o dedo; a classe
          // de animação some para não brigar com ele.
          !drag.isDragging &&
            !draggedOutRef.current &&
            (closing
              ? isBottom
                ? 'animate-sheet-out'
                : 'animate-menu-out'
              : isBottom
                ? 'animate-sheet-in'
                : 'animate-menu-in'),
          className,
        )}
      >
        <div
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={drag.onPointerUp}
          onPointerCancel={drag.onPointerUp}
          className={cn(
            'shrink-0 px-5 sm:px-6',
            titleHidden ? 'pt-3' : 'pb-4 pt-3',
            isDraggable && 'cursor-grab touch-none active:cursor-grabbing',
          )}
        >
          {/* A alça não é enfeite: é o que anuncia que dá para arrastar, e é a
              área de pega no celular. No `center` ela não existe, porque lá
              não há arrasto nenhum para anunciar. */}
          {isDraggable ? (
            <div
              aria-hidden="true"
              className="mx-auto mb-3 h-1 w-9 rounded-full bg-border sm:hidden"
            />
          ) : null}

          <div className={cn('flex items-start justify-between gap-4', titleHidden && 'sr-only')}>
            <div className="min-w-0 space-y-1">
              <h2 id={titleId} className="text-lg font-semibold tracking-tight">
                {title}
              </h2>
              {description ? (
                <p id={descriptionId} className="text-sm text-muted-foreground">
                  {description}
                </p>
              ) : null}
            </div>

            {/* O "X" só existe na FOLHA. O alerta central já tem dois verbos
                explícitos no rodapé; um terceiro jeito de sair, sem rótulo,
                só acrescenta uma escolha a uma pergunta de sim ou não. */}
            {dismissible && isBottom ? (
              <button
                type="button"
                onClick={requestClose}
                aria-label="Fechar"
                className={cn(
                  '-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground',
                  'transition-[color,background-color,transform] duration-fast ease-out active:scale-[0.92]',
                  'hover:bg-accent hover:text-foreground',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                )}
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Sem conteúdo, sem caixa: um alerta que é só pergunta e botões não
            pode carregar um vão vazio entre os dois. */}
        {children ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-6">{children}</div>
        ) : null}

        {footer ? (
          <div className="shrink-0 border-t border-border bg-card px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Arrastar a folha para baixo para fechá-la.
 *
 * Três detalhes fazem a diferença entre "arrasta" e "obedece":
 *
 * 1. **1:1 com o dedo, sem passar pelo React.** O `transform` é escrito direto
 *    no nó; um `setState` por `pointermove` re-renderizaria a folha inteira
 *    sessenta vezes por segundo e o atraso apareceria como borracha.
 * 2. **Elástico para cima.** A folha não sobe além do topo — ela resiste cada
 *    vez mais. Parede dura lê como travado; resistência lê como "não tem mais".
 * 3. **A decisão é da VELOCIDADE, não da posição.** Um peteleco curto e rápido
 *    fecha; um arrasto longo e parado volta. `project()` é a função de
 *    desaceleração do iOS — ela responde "onde isso ia parar?", e é a resposta
 *    que decide.
 *
 * Sem biblioteca de mola (regra 13): a volta é uma transição com a curva de
 * saída da casa. Uma mola de verdade herdaria a velocidade do dedo na volta
 * também; aqui ela herda só na ida, que é onde o olho repara.
 */
function useSheetDrag({
  enabled,
  panelRef,
  onDismiss,
}: {
  enabled: boolean;
  panelRef: React.RefObject<HTMLDivElement>;
  onDismiss: () => void;
}) {
  const stateRef = useRef<{
    pointerId: number;
    startY: number;
    offset: number;
    samples: { y: number; t: number }[];
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!enabled || (event.pointerType === 'mouse' && event.button !== 0)) return;

    /* O "X" mora DENTRO da área de arrasto, e `setPointerCapture` redireciona
       o `click` para quem capturou o ponteiro — não para o botão sob o dedo
       (Pointer Events §10). Resultado: o "X" não fechava a folha, e só o
       "Voltar e ajustar", que vive no rodapé, funcionava. Sair antes de
       capturar devolve o clique ao controle; o resto do cabeçalho continua
       arrastável. */
    if ((event.target as Element).closest('button,a,input,select,textarea,[role="button"]')) {
      return;
    }

    const panel = panelRef.current;
    if (!panel) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    stateRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      offset: 0,
      samples: [{ y: event.clientY, t: performance.now() }],
    };
    setIsDragging(true);
    panel.style.transition = 'none';
    panel.style.willChange = 'transform';
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const state = stateRef.current;
    const panel = panelRef.current;
    if (!state || !panel || event.pointerId !== state.pointerId) return;

    const height = panel.offsetHeight || 1;
    const raw = event.clientY - state.startY;
    const offset = raw >= 0 ? raw : -rubberband(-raw, height);

    state.offset = offset;
    state.samples.push({ y: event.clientY, t: performance.now() });
    if (state.samples.length > 6) state.samples.shift();

    panel.style.transform = `translateY(${offset}px)`;
  }

  function onPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    const state = stateRef.current;
    const panel = panelRef.current;
    if (!state || !panel || event.pointerId !== state.pointerId) return;

    stateRef.current = null;
    setIsDragging(false);
    panel.style.willChange = '';

    const height = panel.offsetHeight || 1;
    const velocity = releaseVelocity(state.samples);
    const projected = state.offset + project(velocity);

    if (projected > height * 0.35) {
      panel.style.transition = 'transform 220ms cubic-bezier(0.32, 0, 0.67, 0)';
      panel.style.transform = `translateY(${height}px)`;
      onDismiss();
      return;
    }

    panel.style.transition = 'transform 320ms cubic-bezier(0.33, 1, 0.68, 1)';
    panel.style.transform = 'translateY(0px)';
  }

  return { isDragging, onPointerDown, onPointerMove, onPointerUp };
}

/** px/s no instante em que o dedo soltou, medidos na janela mais recente. */
function releaseVelocity(samples: { y: number; t: number }[]): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1]!;
  const first = samples.find((sample) => last.t - sample.t <= 120) ?? samples[0]!;
  const elapsed = last.t - first.t;
  return elapsed > 0 ? ((last.y - first.y) / elapsed) * 1000 : 0;
}

/** Onde isso pararia sozinho (desaceleração exponencial, a mesma do scroll). */
function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/** Quanto mais além do limite, menos a folha acompanha o dedo. */
function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  return (overshoot * dimension * constant) / (dimension + constant * overshoot);
}
